import React, { useEffect } from "react";
import { MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import type {
  NETProcedure as NETProcedureValues,
  NetCountMode,
  TargetFormValues,
} from "../../TargetBuilderPanel.types";
import {
  NET_COUNT_MODE_OPTIONS,
  getNetCountMode,
  isNETProcedure,
} from "../../TargetBuilderPanel.types";
import { getHiddenPromptHierarchyNetPatch } from "./promptHierarchyNormalization";
import PromptHierarchyEditor from "./PromptHierarchyEditor";
import TeachingProcedureCommonFields from "./TeachingProcedureCommonFields";

interface NETProcedureProps {
  hidePromptHierarchy?: boolean;
  hideReinforcement?: boolean;
  /** When true, hide Teaching Opportunities per Session (caregiver goals use Practice Opportunities instead). */
  hideTeachingOpportunities?: boolean;
  fieldsOptional?: boolean;
}

const NETProcedure: React.FC<NETProcedureProps> = ({
  hidePromptHierarchy,
  hideReinforcement,
  hideTeachingOpportunities,
  fieldsOptional,
}) => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  if (!isNETProcedure(values.teaching_procedure)) return null;

  const procedure: NETProcedureValues = values.teaching_procedure;
  const update = (partial: Partial<NETProcedureValues>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  // Count-type selection applies to frequency + NET (occurrences /
  // C-I / PH) and latency + NET (C-I / PH — no occurrence primitive for
  // latency).
  const l1 = values.l1_dimension;
  const hasCountModeSelector = (l1 === "frequency" || l1 === "latency") && !hidePromptHierarchy;
  const countMode = getNetCountMode(procedure, l1, values.target_type);
  const countModeOptions =
    l1 === "latency"
      ? NET_COUNT_MODE_OPTIONS.filter((option) => option.value !== "occurrence")
      : NET_COUNT_MODE_OPTIONS;

  const showPromptHierarchy =
    !hidePromptHierarchy && (hasCountModeSelector ? countMode === "prompt-hierarchy" : true);

  useEffect(() => {
    if (!hidePromptHierarchy) return;
    const patch = getHiddenPromptHierarchyNetPatch(procedure, l1);
    if (!patch) return;
    update(patch);
  }, [hidePromptHierarchy, l1, procedure.counter_type, procedure.prompt_hierarchy?.levels?.length]);

  return (
    <Stack gap={6}>
      <TeachingProcedureCommonFields />

      <FieldGroup
        label="Opportunity Definition"
        description="What constitutes a natural teaching opportunity for this target?"
        required={!fieldsOptional}
      >
        <TextField
          multiline
          minRows={2}
          fullWidth
          placeholder="e.g., 'Any time the learner shows interest in a toy or activity during play'"
          value={procedure.opportunity_definition}
          onChange={(e) => update({ opportunity_definition: e.target.value })}
        />
      </FieldGroup>

      {hasCountModeSelector && (
        <FieldGroup
          label="Count Type"
          description="How will responses be recorded during natural opportunities? You collect raw data — percentages and other metrics are calculated for you."
        >
          <Select
            size="small"
            fullWidth
            value={countMode}
            onChange={(e) => {
              const nextMode = e.target.value as NetCountMode;
              // Clear any configured levels when switching away from
              // prompt-hierarchy mode — getNetCountMode gives a non-empty
              // levels array priority over counter_type (matching the
              // backend), so leftover levels from a prior PH selection would
              // otherwise keep resolving back to "prompt-hierarchy" and the
              // editor would never actually hide.
              update(
                nextMode === "prompt-hierarchy"
                  ? { counter_type: nextMode }
                  : {
                      counter_type: nextMode,
                      prompt_hierarchy: { ...procedure.prompt_hierarchy, levels: [] },
                    }
              );
            }}
            aria-label="Count type"
            renderValue={(selected) =>
              countModeOptions.find((o) => o.value === selected)?.label ?? ""
            }
          >
            {countModeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Stack>
                  <Typography variant="body2">{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.description}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>
      )}

      {showPromptHierarchy && <PromptHierarchyEditor />}

      {!hideReinforcement && (
        <FieldGroup
          label="Reinforcement"
          description="How will correct responses be reinforced during natural contexts?"
        >
          <TextField
            multiline
            minRows={2}
            fullWidth
            placeholder="e.g., 'Provide access to requested item, social praise, and natural consequences'"
            value={procedure.reinforcement_notes}
            onChange={(e) => update({ reinforcement_notes: e.target.value })}
          />
        </FieldGroup>
      )}

      {!hideTeachingOpportunities && (
        <FieldGroup
          label="Teaching Opportunities per Session"
          description="What is the minimum teaching opportunities you plan for each session?"
          required={!fieldsOptional}
        >
          <TextField
            size="small"
            type="number"
            value={procedure.teaching_opportunities_per_session ?? ""}
            onChange={(e) => update({ teaching_opportunities_per_session: e.target.value })}
            placeholder="e.g., 1"
            aria-label="Teaching opportunities per session"
            inputProps={{ min: 1 }}
            InputProps={{ sx: { width: 120 } }}
          />
        </FieldGroup>
      )}
    </Stack>
  );
};

export default NETProcedure;
